import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Divider,
  Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import React, { useEffect, useState } from "react";
import { getBoxSizeDisplay } from "../../../utils/boxPricing";


import { useDispatch, useSelector } from "react-redux";
import {
  cancelOrder,
  confirmOrder,
  deliveredOrder,
  getOrders,
  shipOrder,
  updateShippingInfo,
} from "../../../Redux/Admin/Orders/Action";

const OrdersTable = () => {
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const { adminsOrder } = useSelector((store) => store);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Cancel confirmation modal state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [editTrackingNumber, setEditTrackingNumber] = useState("");
  const [editShippingCarrier, setEditShippingCarrier] = useState("UPS");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatch(getOrders({ jwt }));
  }, [jwt, adminsOrder.delivered, adminsOrder.shipped, adminsOrder.confirmed, adminsOrder.canceled, adminsOrder.shippingInfoUpdated]);



  // Cancel confirmation handlers
  const handleOpenCancelDialog = (order) => {
    setOrderToCancel(order);
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setOrderToCancel(null);
  };

  const handleConfirmCancel = () => {
    if (orderToCancel) {
      dispatch(cancelOrder(orderToCancel.id));
    }
    handleCloseCancelDialog();
  };

  // Edit modal handlers
  const handleOpenEditModal = (order) => {
    setSelectedOrder(order);
    setEditStatus(order.orderStatus || "");
    setEditTrackingNumber(order.trackingNumber || "");
    setEditShippingCarrier(order.shippingCarrier || "UPS");
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedOrder(null);
    setEditStatus("");
    setEditTrackingNumber("");
    setEditShippingCarrier("UPS");
  };

  const handleSaveOrderStatus = async () => {
    if (!selectedOrder) return;

    const statusChanged = editStatus && editStatus !== selectedOrder.orderStatus;
    const shippingChanged =
      editTrackingNumber !== (selectedOrder.trackingNumber || "") ||
      editShippingCarrier !== (selectedOrder.shippingCarrier || "UPS");

    // Dispatch status update first and await completion to avoid race condition
    if (statusChanged) {
      if (editStatus === "CONFIRMED") {
        await dispatch(confirmOrder(selectedOrder.id));
      } else if (editStatus === "SHIPPED") {
        await dispatch(shipOrder(selectedOrder.id));
      } else if (editStatus === "DELIVERED") {
        await dispatch(deliveredOrder(selectedOrder.id));
      } else if (editStatus === "CANCELLED") {
        await dispatch(cancelOrder(selectedOrder.id));
      }
    }

    // Save shipping info after status update completes
    if (shippingChanged) {
      await dispatch(updateShippingInfo(selectedOrder.id, editTrackingNumber, editShippingCarrier));
    }

    handleCloseEditModal();
  };

  // Search/Filter function
  const getFilteredOrders = () => {
    if (!adminsOrder?.orders) return [];

    if (!searchQuery.trim()) {
      return adminsOrder.orders;
    }

    const query = searchQuery.toLowerCase().trim();

    return adminsOrder.orders.filter((order) => {
      // Search by ID
      if (order.id?.toString().includes(query)) return true;
      // Search by Order ID
      if (order.orderId?.toLowerCase().includes(query)) return true;
      // Search by First Name
      if (order.user?.firstName?.toLowerCase().includes(query)) return true;
      // Search by Last Name
      if (order.user?.lastName?.toLowerCase().includes(query)) return true;
      // Search by Phone
      if (order.user?.mobile?.includes(query)) return true;
      if (order.shippingAddress?.mobile?.includes(query)) return true;
      // Search by Status
      if (order.orderStatus?.toLowerCase().includes(query)) return true;
      // Search by Order Date
      if (order.orderDate?.toString().includes(query)) return true;

      return false;
    });
  };

  const filteredOrders = getFilteredOrders();



  // Get customer full name
  const getCustomerName = (user) => {
    if (!user) return "N/A";
    return `${user.firstName || ""} ${user.lastName || ""}`.trim() || "N/A";
  };

  // Get phone number
  const getPhoneNumber = (order) => {
    return order.user?.mobile || order.shippingAddress?.mobile || "N/A";
  };

  return (
    <Box>
      <Card className="mt-2">
        <CardHeader
          title="All Orders"
          sx={{
            pt: 2,
            alignItems: "center",
            "& .MuiCardHeader-action": { mt: 0.6 },
          }}
          action={
            <TextField
              size="small"
              placeholder="Search orders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
          }
        />
        <TableContainer>
          <Table sx={{ minWidth: 800 }} aria-label="table in dashboard">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Id</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Order Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Tracking #</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Carrier</TableCell>
                <TableCell sx={{ textAlign: "center", fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ textAlign: "center", fontWeight: 'bold' }}>Update</TableCell>
                <TableCell sx={{ textAlign: "center", fontWeight: 'bold' }}>Cancel</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders?.map((item, index) => (
                <TableRow
                  hover
                  key={item.id}
                  sx={{ "&:last-of-type td, &:last-of-type th": { border: 0 } }}
                >
                  <TableCell>{item.id}</TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem !important",
                      }}
                    >
                      {getCustomerName(item.user)}
                    </Typography>
                  </TableCell>
                  <TableCell>{getPhoneNumber(item)}</TableCell>
                  <TableCell>${item.totalPrice?.toFixed(2) || "0.00"}</TableCell>
                  <TableCell>{item.orderDate || "N/A"}</TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 150,
                      }}
                      title={item.trackingNumber || ""}
                    >
                      {item.trackingNumber || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.shippingCarrier || "—"}</TableCell>
                  <TableCell className="text-white">
                    <Chip
                      sx={{
                        color: "white !important",
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                      label={item.orderStatus}
                      size="small"
                      color={
                        item.orderStatus === "PENDING"
                          ? "info"
                          : item.orderStatus === "DELIVERED"
                            ? "success"
                            : item.orderStatus === "CANCELLED"
                              ? "error"
                              : "secondary"
                      }
                      className="text-white"
                    />
                  </TableCell>
                  <TableCell
                    sx={{ textAlign: "center" }}
                    className="text-white"
                  >
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenEditModal(item)}
                      title="Edit Order"
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell
                    sx={{ textAlign: "center" }}
                    className="text-white"
                  >
                    <IconButton
                      color="warning"
                      onClick={() => handleOpenCancelDialog(item)}
                      title="Cancel Order"
                      disabled={item.orderStatus === "CANCELLED" || item.orderStatus === "DELIVERED"}
                    >
                      <CancelIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">
          Cancel Order
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            Are you sure you want to cancel this order? The order will be marked as "Cancelled" and the customer will be notified.
          </DialogContentText>
          {orderToCancel && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Order ID:</strong> {orderToCancel.orderId || `#${orderToCancel.id}`}
              </Typography>
              <Typography variant="body2">
                <strong>Customer:</strong> {getCustomerName(orderToCancel.user)}
              </Typography>
              <Typography variant="body2">
                <strong>Total Price:</strong> ${orderToCancel.totalPrice?.toFixed(2) || "0.00"}
              </Typography>
              <Typography variant="body2">
                <strong>Current Status:</strong> {orderToCancel.orderStatus}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} color="inherit">
            Go Back
          </Button>
          <Button onClick={handleConfirmCancel} color="error" variant="contained">
            Cancel Order
          </Button>
        </DialogActions>
      </Dialog>


      {/* Edit Order Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
        aria-labelledby="edit-order-dialog-title"
        PaperProps={{
          sx: {
            maxHeight: '95vh',
            overflow: 'visible',
          }
        }}
      >
        <DialogTitle id="edit-order-dialog-title" sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Order Details</Typography>
            <IconButton onClick={handleCloseEditModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto', pt: '8px !important' }}>
          {selectedOrder && (
            <Box>
              {/* ─── ORDER ITEMS ─── at the very top */}
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                Order Items
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ bgcolor: "grey.50", p: 1.5, borderRadius: 1, mb: 3 }}>
                {selectedOrder.orderItems?.map((orderItem, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      py: 1,
                      borderBottom: idx < (selectedOrder.orderItems?.length || 0) - 1 ? '1px solid' : 'none',
                      borderColor: 'grey.200',
                    }}
                  >
                    <Avatar
                      src={orderItem.product?.imageUrl}
                      alt={orderItem.product?.title}
                      variant="rounded"
                      sx={{ width: 48, height: 48, mr: 2 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="600">
                        {orderItem.product?.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getBoxSizeDisplay(orderItem.size)} &nbsp;·&nbsp; Qty: {orderItem.quantity} &nbsp;·&nbsp; ${(parseFloat(orderItem.discountedPrice) || parseFloat(orderItem.price) || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* ─── ORDER INFORMATION (read-only summary row) ─── */}
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                Order Information
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Order ID"
                    value={selectedOrder.orderId || `#${selectedOrder.id}`}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Order Date"
                    value={selectedOrder.orderDate || "N/A"}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Total Price"
                    value={`$${selectedOrder.totalPrice?.toFixed(2) || "0.00"}`}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Delivery Date"
                    value={selectedOrder.deliveryDate || "Not set"}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>

              {/* ─── EDITABLE: UPDATE ORDER STATUS ─── */}
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: "primary.main" }}>
                Update Order Status
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="order-status-label">Order Status</InputLabel>
                    <Select
                      labelId="order-status-label"
                      value={editStatus}
                      label="Order Status"
                      onChange={(e) => setEditStatus(e.target.value)}
                    >
                      <MenuItem value="PENDING">PENDING</MenuItem>
                      <MenuItem value="PLACED">PLACED</MenuItem>
                      <MenuItem value="CONFIRMED">CONFIRMED</MenuItem>
                      <MenuItem value="SHIPPED">SHIPPED</MenuItem>
                      <MenuItem value="DELIVERED">DELIVERED</MenuItem>
                      <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* ─── EDITABLE: SHIPPING / TRACKING ─── */}
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: "primary.main" }}>
                Shipping / Tracking
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="UPS Tracking Number"
                    value={editTrackingNumber}
                    onChange={(e) => setEditTrackingNumber(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g. 1Z999AA10123456784"
                    helperText="Enter the UPS tracking number for this order"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="shipping-carrier-label">Shipping Carrier</InputLabel>
                    <Select
                      labelId="shipping-carrier-label"
                      value={editShippingCarrier}
                      label="Shipping Carrier"
                      onChange={(e) => setEditShippingCarrier(e.target.value)}
                    >
                      <MenuItem value="UPS">UPS</MenuItem>
                      <MenuItem value="FedEx">FedEx</MenuItem>
                      <MenuItem value="USPS">USPS</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* ─── CUSTOMER INFO + SHIPPING ADDRESS (compact row) ─── */}
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                Customer &amp; Shipping
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Customer Name"
                    value={getCustomerName(selectedOrder.user)}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Email"
                    value={selectedOrder.user?.email || "N/A"}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Phone"
                    value={getPhoneNumber(selectedOrder)}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Street Address"
                    value={selectedOrder.shippingAddress?.streetAddress || "N/A"}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="City"
                    value={selectedOrder.shippingAddress?.city || "N/A"}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="State"
                    value={selectedOrder.shippingAddress?.state || "N/A"}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Postal Code"
                    value={selectedOrder.shippingAddress?.zipCode || "N/A"}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEditModal} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveOrderStatus}
            color="primary"
            variant="contained"
            disabled={
              !editStatus || (
                editStatus === selectedOrder?.orderStatus &&
                editTrackingNumber === (selectedOrder?.trackingNumber || "") &&
                editShippingCarrier === (selectedOrder?.shippingCarrier || "UPS")
              )
            }
          >
            Update Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdersTable;
